import {
  FuzzySearchResourceType,
  type FuzzySearchResult,
} from "@repo/app-types/entities/search";
import {
  buildProjectUrlPath,
  ProjectDetailsTabPath,
} from "@repo/app-types/helpers/adminAppRoutes";
import { appPaths } from "../../app/appPaths.js";

export function buildKeyForSearchResult(result: FuzzySearchResult): string {
  return `${result.resource_type}:${result.resource_id}`;
}

interface BuildPathForSearchResultOptions {
  isTrainingSearchContext: boolean;
}

export function buildPathForSearchResult(
  result: FuzzySearchResult,
  options: BuildPathForSearchResultOptions,
): string | undefined {
  switch (result.resource_type) {
    case FuzzySearchResourceType.TeammateWithGradings: {
      const { playbook_id, teammate_id } = result.search_attributes;
      if (!playbook_id || !teammate_id) {
        break;
      }
      return appPaths.teammate.build({
        playbookId: playbook_id,
        teammateId: teammate_id,
      });
    }
    case FuzzySearchResourceType.TeammateAccountGrading: {
      // For grading results the resource_id IS the grading id.
      const { playbook_id } = result.search_attributes;
      if (!playbook_id || !result.resource_id) {
        break;
      }

      return appPaths.playbookCall.build({
        playbookId: playbook_id,
        gradingId: result.resource_id,
      });
    }
    case FuzzySearchResourceType.Interview: {
      const { project_id } = result.search_attributes;
      if (!project_id) {
        break;
      }

      if (options.isTrainingSearchContext) {
        return buildProjectUrlPath(
          project_id,
          ProjectDetailsTabPath.Participants,
          result.resource_id,
        );
      }

      return buildProjectUrlPath(
        project_id,
        ProjectDetailsTabPath.Interviews,
        result.resource_id,
      );
    }
  }

  return undefined;
}

export function findTopSearchResult(
  searchResults: readonly FuzzySearchResult[] | undefined,
) {
  const firstSearchResult = searchResults?.at(0);
  const nextSearchResult = searchResults?.at(1);

  // The first search result must have a 10% higher similarity score than the next to be considered a top search result
  return firstSearchResult &&
    (!nextSearchResult ||
      firstSearchResult.similarity_score / nextSearchResult.similarity_score >
        1.1)
    ? firstSearchResult
    : undefined;
}
